import { action, observable, computed } from 'mobx';
import isEqual from 'lodash/isEqual';

import type { EntryDrugParams } from 'chaika-frontend-types/frontend-cms';
import { DictionaryNames, FieldStatus } from 'chaika-enums';

import { store } from '@store';
import { DateHelper } from '@utils/DateHelper';

export class EntryDrug {
    @observable
    public id: string;

    @observable
    public activeSubstance: string;

    @observable
    public tradeName: string;

    @observable
    public frequencyIfReception: string;

    @observable
    public directions: string;

    @observable
    public startDate: string;

    @observable
    public endDate: string;

    @observable
    public status?: FieldStatus;

    constructor({
        id,
        activeSubstance,
        tradeName,
        frequencyIfReception,
        directions,
        startDate,
        endDate,
        status,
    }: EntryDrugParams) {
        this.id = id;
        this.activeSubstance = activeSubstance;
        this.tradeName = tradeName;
        this.frequencyIfReception = frequencyIfReception;
        this.directions = directions;
        this.startDate = startDate;
        this.endDate = endDate;
        this.status = status;
    }

    @computed
    public get isActive(): boolean {
        return !this.endDate || DateHelper.isAfter(this.endDate, Date.now());
    }

    @computed
    public get isFavorite(): boolean {
        const { id, status, endDate, startDate, ...drug } = this.serialize();
        const {
            authUser: {
                favorites: { drugFavoriteList },
            },
        } = store;

        return drugFavoriteList.values.some(({ data }) => isEqual(data, drug));
    }

    public get isDeleted(): boolean {
        return this.status === FieldStatus.Deleted;
    }

    public getTradeNameTitle(): string {
        const dictionary = store.dictionary.getDictionary(DictionaryNames.DrugTradeNames);
        return (this.tradeName && dictionary.get(this.tradeName)?.title) || '';
    }

    public getActiveSubstanceTitle(): string {
        const dictionary = store.dictionary.getDictionary(DictionaryNames.ElementDrug);
        return (this.activeSubstance && dictionary.get(this.activeSubstance)?.title) || '';
    }

    @action
    public update(
        { activeSubstance, tradeName, frequencyIfReception, directions, startDate, endDate }: Partial<EntryDrugParams>,
        onFinish?: () => void,
    ): void {
        this.activeSubstance = activeSubstance ?? this.activeSubstance;
        this.tradeName = tradeName ?? this.tradeName;
        this.frequencyIfReception = frequencyIfReception ?? this.frequencyIfReception;
        this.directions = directions ?? this.directions;
        this.startDate = startDate ?? this.startDate;
        this.endDate = endDate ?? this.endDate;

        if (onFinish) {
            onFinish();
        }
    }

    public serialize(): EntryDrugParams {
        return {
            id: this.id,
            activeSubstance: this.activeSubstance,
            tradeName: this.tradeName,
            frequencyIfReception: this.frequencyIfReception,
            directions: this.directions,
            startDate: this.startDate,
            endDate: this.endDate,
            status: this.status,
        };
    }
}
